<script setup>
import * as yup from 'yup';
import { inject, ref } from 'vue';
import { Field, useForm, useField, ErrorMessage } from 'vee-validate';
import { __, SelfcareAccountChangeDetails, requestZipCode, decodeHTMLEntities, isNumber, handleNumberFieldPaste, inputValidEmail } from '@/utils';
import Flex from '@components/flex/flex.vue';
import Grid from '@components/grid/grid.vue';
import FieldCity from '@components/fieldCity/fieldCity.vue';
import FieldStreet from '@components/fieldStreet/fieldStreet.vue';
import Loader from '@components/loader.vue';
import FraudModal from "@components/FraudModal.vue";
import { vMaska } from 'maska/vue';

const { details, setUpdatedDetails } = inject('account');
const accountDetails = ref(details.value)
const loading = ref(false);
const cityCode = ref(accountDetails.value?.address?.city_code);
const reqField = __('Required field')
const streetCode = ref(accountDetails.value?.address?.street_code);
const loadingUpdatedDetails = ref(false);
const showError = ref(false);
const fraudAccount = ref(false);

const emit = defineEmits(["updatedDetails", "close"]);

const schema = yup.object({
    'first_name': yup.string().required(reqField).typeError(reqField),
    'last_name': yup.string().required(reqField).typeError(reqField),
    'city': yup.string().required(reqField).typeError(reqField),
    'street': yup.string().required(reqField).typeError(reqField),
    'house_number': yup.number().required(reqField).typeError(reqField),
});

const { handleSubmit, values, setFieldValue } = useForm({
	validationSchema: schema,
    initialValues: {
        first_name: decodeHTMLEntities(accountDetails.value?.first_name) || '',
        last_name: decodeHTMLEntities(accountDetails.value?.last_name),
        city: decodeHTMLEntities(accountDetails.value?.address?.city),
        street: decodeHTMLEntities(accountDetails.value?.address?.street),
        house_number: accountDetails.value?.address?.house_number,
        floor: accountDetails.value?.address?.floor,
        apartment_number: accountDetails.value?.address?.apartment_number,
        phone: accountDetails.value?.phone,
        personal_id: accountDetails.value?.personal_id,
        email: accountDetails.value?.email,
        house_enter: accountDetails.value?.address?.house_enter,
        zip_code: accountDetails.value?.address?.zip_code,
        city_code: accountDetails.value?.address?.city_code,
        street_code: accountDetails.value?.address?.street_code,
    },
});

const { handleChange: handeCompanyCityCode } = useField('city');
const { handleChange: handleCompanyStreetCode } = useField('street');
const handleSelectedCity = ({ code }) => {
    handeCompanyCityCode(code)
    cityCode.value = code;
    setFieldValue('city_code', code);
    setFieldValue('street', '');
    setFieldValue('house_number', '')
}

const makeZipRequest = async () => {
    loading.value = true;
    const [data, error] = await requestZipCode({
        city_code: parseInt(cityCode.value),
        street_code: parseInt(streetCode.value),
        house_number: parseInt(values.house_number),
        house_enter: parseInt(values.house_enter)
    });
    loading.value = false;
    if (error) {
        console.log(error)
    }
    const { response } = JSON.parse(data?.data);
    if(response.length > 0) {
        setFieldValue('zip_code', response[0].name);
    }
}

const handleSelectedStreet = ({ code }) => {
    handleCompanyStreetCode(code)
    streetCode.value = code;
    setFieldValue('street_code', code);
    setFieldValue('house_number', '')
    makeZipRequest();
}

const onSubmit = handleSubmit(async (formValues) => {
    loadingUpdatedDetails.value = true;
    try {
        const response = await SelfcareAccountChangeDetails(formValues);
        if(response?.data?.response?.error && response?.data?.response?.errorMessage === 'fraudulent-account'){
            fraudAccount.value = true;
        }
        if(response?.data?.response?.id){
            showError.value = false;
            accountDetails.value = response.data.response
            setUpdatedDetails(response.data.response);
            emit("updatedDetails", response.data.response);
        }else{
            showError.value = true;
        }
    } catch (error) {
        console.error('Error updating detail', error);
        showError.value = true;
    }
    emit("close");
    loadingUpdatedDetails.value = false;
})
</script>
<template>
     <FraudModal class="popup--contact" :isActive="fraudAccount" @close="fraudAccount = false"></FraudModal>
    <form class="subscription-form">
        <Flex :gap="16">
            <Flex :gap="16" class="personal-info">
                <div class="field">
                    <ErrorMessage name="first_name" as="span" class="field__error" />
                    <label for="first_name">{{__('First name')}} *</label>
                    <Field type="text" name="first_name" id="first_name" />
                </div>
                <div class="field">
                    <ErrorMessage name="last_name" as="span" class="field__error" />
                    <label for="last_name">{{__('Last Name')}} *</label>
                    <Field type="text" name="last_name" id="last_name" />
                </div>
            </Flex>
            <div class="field">
                <ErrorMessage name="personal_id" as="span" class="field__error" />
                <label for="personal_id">{{__('ID')}} *</label>
                <Field type="text" name="personal_id" id="personal_id" disabled />
            </div>
            <div class="field">
                <ErrorMessage name="email" as="span" class="field__error" />
                <label for="email">{{__('Email')}} *</label>
                <Field type="text" name="email" id="email" @keypress="inputValidEmail" />
            </div>
            <div class="field">
                <ErrorMessage name="phone" as="span" class="field__error" />
                <label for="phone">{{__('Contact Phone Number')}} *</label>
                <Field 
                    type="text"
                    name="phone"
                    id="phone"
                    v-maska
					data-maska="###-##########"
                    @keypress="isNumber($event)" maxlength="11"
                    @paste="handleNumberFieldPaste"
                />
            </div>
        </Flex>
        <h3>{{ __("Entering the subscriber's address") }}</h3>
        <Flex :gap="25">
            <FieldCity
                name="city"
                :label="`${__('Settlement')} *`"
                @selected="handleSelectedCity"
                class="field--larger"
            />
            <FieldStreet
                name="street"
                :label="`${__('Street / P.O.')} *`"
                :city_code="cityCode"
                @selected="handleSelectedStreet"
                class="field--larger"
            />
            <Grid :columns="4" :gap="25" >
                <div class="field">
                    <ErrorMessage name="house_number" as="span" class="field__error" />
                    <label for="house_number">{{__('No. Home')}} *</label>
                    <Field type="text" name="house_number" id="house_number" />
                </div>
                <div class="field">
                    <ErrorMessage name="apartment_number" as="span" class="field__error" />
                    <label for="apartment_number">{{__('Apartment')}}</label>
                    <Field type="text" name="apartment_number" id="apartment_number" />
                </div>
                <div class="field">
                    <ErrorMessage name="floor" as="span" class="field__error" />
                    <label for="floor">{{__('Floor')}}</label>
                    <Field type="text" name="floor" id="floor" />
                </div>
                <div class="field">
                    <ErrorMessage name="house_enter" as="span" class="field__error" />
                    <label for="house_enter">{{__('Entrance')}}</label>
                    <Field type="text" name="house_enter" id="house_enter" />
                </div>
            </Grid>
        </Flex>
        <div class="subscription-form-actions">
            <button class="btn" type="submit" @click="onSubmit" :disabled="loading">{{__('Save')}}</button> <Loader color="#2563eb" v-if="loadingUpdatedDetails"/>

                <span role="alert" class="field__error" style="color:red" v-if="showError">
                    {{ __('One or more fields contain incorrect information. Please double-check the required fields') }}
                </span>

            <button type="button" class="link-underline" @click="$emit('close')">{{ __('cancelation') }}</button>
        </div>
    </form>
</template>